import React from 'react';
import { FuelingSummaryDetailDTO } from '../dtos/FuelingSummaryDetailDTO';
import { CommodityDetail } from 'features/contracts/enums/commodityDetail';
import InfoItemMobile from 'features/shared/components/infoItem/InfoItemMobile';
import NumberFormatter from 'features/shared/components/formatters/NumberFormatter';
import { useTranslation } from 'react-i18next';
import PriceFormatter from 'features/shared/components/formatters/PriceFormatter';
import RoundButton from 'features/shared/components/buttons/RoundButton';
import { FuelingTransactionViewType } from '../enums/fuelingTransactionViewType';

type IdentificationType = {
  value: string;
  translete: string;
  name: FuelingTransactionViewType;
  id: string;
}

type Props = {
  summaryDetail: FuelingSummaryDetailDTO;
  commodityDetail: CommodityDetail;
  onDownloadClick: (entityType?: string, entityId?: string) => void;
}

const FuelingSummaryDetail = ({ summaryDetail, commodityDetail, onDownloadClick }: Props) => {
  // transaltions
  const tLabels = useTranslation("fueling\\fueling").t;
  const tViewTypes = useTranslation("fueling\\enums\\fuelingTransactionViewType").t;

  // handlers
  const downloadTransactionsExcelHandler = () => { 
    const identification = getIdentification();
    onDownloadClick(`${identification.name}`, identification.id);
  }

  // helpers
  const getIdentification = (): IdentificationType => { 
    if (summaryDetail.fuelingCustomer) {
      return {
        value: summaryDetail.fuelingCustomer,
        translete: tViewTypes(FuelingTransactionViewType.FuelingCustomer),
        id: summaryDetail.fuelingCustomer,
        name: FuelingTransactionViewType.FuelingCustomer,
      };
    } else if (summaryDetail.fuelingCard) {
      return {
        value: summaryDetail.fuelingCard,
        translete: tViewTypes(FuelingTransactionViewType.FuelingCard),
        id: summaryDetail.fuelingCard,
        name: FuelingTransactionViewType.FuelingCard,
      };
    } else if (summaryDetail.stationName) {
      return {
        value: summaryDetail.stationName!,
        translete: tViewTypes(FuelingTransactionViewType.Station),
        id: summaryDetail.stationId!,
        name: FuelingTransactionViewType.Station,
      };
    }

    return {
      id: "",
      name: FuelingTransactionViewType.Undefined,
      value: "",
      translete: ""
    };
  }

  // sub-renders
  const renderPrice = () => {
    if (summaryDetail.totalAmount && summaryDetail.amountCurrency) {
      return (
        <PriceFormatter
          amount={summaryDetail.totalAmount}
          currency={summaryDetail.amountCurrency}
        />
      );
    } else {
      return <>-</>
    }
  };

  const renderFueling = () => { 
    return (
      <span>
          <NumberFormatter amount={summaryDetail.fuelingSum!} decimalPlaces={3} />
          <span className="d-sm-none small"> {summaryDetail.fuelingUnit}</span>
        </span>
      );
  }

  // render
  return (
    <div className="row no-gutters align-items-center">
      <div className="col">
        <div className="row no-gutters align-items-center">
          <div className="col-sm">
          <InfoItemMobile
              title={getIdentification().translete + ":"}
              value={getIdentification().value}
              hideTitleOn="sm"
              valueClassName="mr-auto mr-sm-0"
            />
          </div>
          <div className="col-sm text-sm-center">
            <InfoItemMobile
              title={tLabels(`fuelingMonthSummaryHeader.fuelingSum.${commodityDetail}`) + ":"}
              value={renderFueling()}
              hideTitleOn="sm"
              valueClassName="mr-auto mr-sm-0"
            />
          </div>
          <div className="col-sm text-sm-center">
            <InfoItemMobile
              title={tLabels(`fuelingMonthSummaryHeader.fuelingCount.${commodityDetail}`) + ":"}
              value={`${summaryDetail.fuelingCount}`}
              hideTitleOn="sm"
              valueClassName="mr-auto mr-sm-0"
            />
          </div>
          <div className="col-sm text-sm-center">
            <InfoItemMobile
              title={tLabels("fuelingMonthSummaryHeader.totalAmount") + ":"}
              value={renderPrice()}
              hideTitleOn="sm"
              valueClassName="mr-auto mr-sm-0"
            />
          </div>
        </div>
      </div>
      <div className="col-auto pl-3">
        <RoundButton 
          icon="file-excel"
          onClickHandler={downloadTransactionsExcelHandler}
        />
      </div>
    </div>
  );
}

export default FuelingSummaryDetail;